import VueRouter from 'vue-router';
import { Jurisdiction } from '@gig/magic-jurisdictions';
import { RouteName } from '../../../routes/routes.constants';
import { getUserJurisdiction } from '../helpers/jurisdiction.helpers';
import { magic } from '@gig/magic-sdk';
import { isRouteActive } from '../helpers/routes';
import {
    USER_PROFILE_DATA,
    USER_REQUIRES_TERMS_ACCEPTANCE_QUERY,
    USER_UPDATE_PREFERENCES_UPDATED,
    Feature,
} from '@gig/magic-messages';
import { IUpdatedPreferencesEventPayload } from '@gig/magic-responsible-gaming-types';
import { IUserProfile } from '@gig/magic-sdk/dist/adapter/users/user';
import {
    CoreXLimitType,
    IUserTransactionLimit,
} from '@gig/magic-sdk/dist/adapter/responsiblegaming/getUserTransactionLimits';
import { getFeatureEnabled } from '@gig/magic-common';

export function initSetLimitsGuard(router: VueRouter) {
    router.beforeEach(async (to, _, next) => {
        const { params, query } = to;

        const userProfile = await window.queryBus.query<IUserProfile>(
            USER_PROFILE_DATA,
        );

        if (!userProfile) {
            return next();
        }

        if (
            isRouteActive(RouteName.SIGN_UP_BANKID, to, router) ||
            isRouteActive(RouteName.USER_INFO, to, router)
        ) {
            return next();
        }

        const userJurisdiction = await getUserJurisdiction();
        switch (userJurisdiction) {
            case Jurisdiction.SGA:
                if (await hasPendingConsents()) {
                    return next();
                }
                const preferences =
                    window.eventBus.getLatest<IUpdatedPreferencesEventPayload>(
                        USER_UPDATE_PREFERENCES_UPDATED,
                    );
                if (preferences?.preferences.limitsConfirmed) {
                    return isRouteActive(RouteName.SET_LIMITS, to, router)
                        ? next({ name: RouteName.HOME })
                        : next();
                }
                break;
            case Jurisdiction.UKGC:
                if (
                    await UKGCShowSetLimits(
                        userProfile?.consents.lastFinancialLimitReviewOnUtc,
                        await getFeatureEnabled(Feature.SINGLE_DEPOSIT_LIMIT),
                    )
                ) {
                    return isRouteActive(RouteName.SET_LIMITS, to, router)
                        ? next()
                        : next({
                              name: RouteName.SET_LIMITS,
                              params,
                              query: { ...query, backTo: to.fullPath },
                          });
                } else {
                    return isRouteActive(RouteName.SET_LIMITS, to, router)
                        ? next({ name: RouteName.HOME })
                        : next();
                }
            default:
                return next();
        }
    });
}

function hasPendingConsents() {
    return window.queryBus.query<boolean>(USER_REQUIRES_TERMS_ACCEPTANCE_QUERY);
}

async function UKGCShowSetLimits(
    lastFinancialLimitReviewOnUtc: string | undefined,
    isSingleDepositLimitEnabled: boolean,
): Promise<boolean> {
    if (await userHasDepositLimits(isSingleDepositLimitEnabled)) {
        return false;
    }

    if (!lastFinancialLimitReviewOnUtc) {
        return true;
    }
    const lastReviewDate = new Date(lastFinancialLimitReviewOnUtc);

    return hasYearPassed(lastReviewDate);
}

async function userHasDepositLimits(isSingleDepositLimitEnabled: boolean) {
    const userLimits = (await magic.invokeCached(
        60 * 5,
        magic.methods.responsibleGaming,
        'getUserTransactionLimits',
    )()) as IUserTransactionLimit[];
    return userLimits.some((limit) => {
        if (isSingleDepositLimitEnabled) {
            return (
                limit.creator === 'User' &&
                (limit.type === CoreXLimitType.DEPOSIT ||
                    limit.type === CoreXLimitType.NET_DEPOSIT)
            );
        }
        return limit.type === CoreXLimitType.DEPOSIT;
    });
}

function hasYearPassed(since: Date) {
    const currentDate = new Date();
    const fromDate = new Date(since);
    fromDate.setFullYear(fromDate.getFullYear() + 1);

    return currentDate >= fromDate;
}
